import React, { useCallback, useMemo, useRef } from 'react';
import { User, Building2, Percent, Wallet } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { SIGNUP_ACCOUNT_TYPES, requiresPersonaApproval } from '../../constants/personas';
import useGooglePlacesAutocomplete from '../../hooks/useGooglePlacesAutocomplete';
import { parseGooglePlaceAddress } from '../../utils/parseGooglePlaceAddress';
import { formatNorthAmericanPhoneInput } from '../../utils/phoneFormat';

const ICONS = {
  investisseur: User,
  courtier_immo: Building2,
  courtier_hypo: Percent,
  preteur_prive: Wallet,
};

export default function AccountTypeSignupFields({
  selectedPersona,
  onPersonaChange,
  verification,
  onVerificationChange,
  disabled = false,
}) {
  const { t } = useLanguage();
  const showVerification =
    requiresPersonaApproval(selectedPersona) &&
    SIGNUP_ACCOUNT_TYPES.some((x) => x.id === selectedPersona && x.selectable);

  const addressOptions = useMemo(
    () => ({
      enabled: !disabled && showVerification,
      componentRestrictions: { country: 'ca' },
      fields: ['address_components', 'formatted_address', 'place_id'],
    }),
    [disabled, showVerification],
  );

  const verificationRef = useRef(verification);
  const onVerificationChangeRef = useRef(onVerificationChange);
  verificationRef.current = verification;
  onVerificationChangeRef.current = onVerificationChange;

  const handlePlaceSelected = useCallback((place) => {
    const parsed = parseGooglePlaceAddress(place);
    if (!parsed.street && !parsed.city && !parsed.postalCode && !parsed.province) {
      return;
    }
    const current = verificationRef.current || {};
    onVerificationChangeRef.current({
      ...current,
      street: parsed.street || current.street,
      city: parsed.city,
      province: parsed.province,
      postalCode: parsed.postalCode,
      country: parsed.country || parsed.countryShort || current.country || 'Canada',
    });
  }, []);

  const streetInputRef = useGooglePlacesAutocomplete(handlePlaceSelected, addressOptions);

  const setField = (key, value) => onVerificationChange({ ...verification, [key]: value });

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('signup.accountType.label')}
        </label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {SIGNUP_ACCOUNT_TYPES.map(({ id, labelKey, selectable }) => {
            const Icon = ICONS[id] || User;
            const isSelected = selectedPersona === id;
            const isLocked = !selectable;
            return (
              <button
                key={id}
                type="button"
                disabled={disabled || isLocked}
                title={isLocked ? t('signup.accountType.comingSoon') : undefined}
                onClick={() => {
                  if (!isLocked) onPersonaChange(id);
                }}
                className={`relative flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-colors ${
                  isLocked
                    ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed opacity-80'
                    : isSelected
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700 disabled:opacity-50'
                }`}
              >
                {isLocked ? (
                  <span className="absolute top-1.5 right-1.5 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                    {t('signup.accountType.comingSoonBadge')}
                  </span>
                ) : null}
                <Icon className="w-6 h-6 shrink-0" />
                <span className="text-xs font-medium text-center leading-tight">{t(labelKey)}</span>
              </button>
            );
          })}
        </div>
        {SIGNUP_ACCOUNT_TYPES.some((x) => !x.selectable) ? (
          <p className="mt-2 text-xs text-gray-500">{t('signup.accountType.teaserHint')}</p>
        ) : null}
      </div>

      {showVerification ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-4 space-y-3">
          <p className="text-sm text-amber-900">{t('signup.verification.intro')}</p>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('signup.verification.companyName')}
            </label>
            <input
              type="text"
              value={verification.companyName || ''}
              disabled={disabled}
              onChange={(e) => setField('companyName', e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('signup.verification.phone')}
            </label>
            <input
              type="tel"
              value={verification.phone || ''}
              disabled={disabled}
              onChange={(e) =>
                setField('phone', formatNorthAmericanPhoneInput(e.target.value))
              }
              className="w-full px-3 py-2 border rounded text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('signup.verification.address')}
            </label>
            <input
              ref={streetInputRef}
              type="text"
              value={verification.street || ''}
              disabled={disabled}
              onChange={(e) => setField('street', e.target.value)}
              placeholder={t('profile.addressStreet')}
              className="w-full px-3 py-2 border rounded text-sm mb-2"
              required
            />
            <div className="grid grid-cols-1 gap-2">
              <input
                type="text"
                value={verification.city || ''}
                disabled={disabled}
                onChange={(e) => setField('city', e.target.value)}
                placeholder={t('profile.addressCity')}
                className="w-full px-3 py-2 border rounded text-sm"
                required
              />
              <input
                type="text"
                value={verification.province || ''}
                disabled={disabled}
                onChange={(e) => setField('province', e.target.value)}
                placeholder={t('profile.addressProvince')}
                className="w-full px-3 py-2 border rounded text-sm"
                required
              />
            </div>
            <div className="grid grid-cols-1 gap-2 mt-2">
              <input
                type="text"
                value={verification.postalCode || ''}
                disabled={disabled}
                onChange={(e) => setField('postalCode', e.target.value)}
                placeholder={t('profile.addressPostalCode')}
                className="w-full px-3 py-2 border rounded text-sm"
                required
              />
              <input
                type="text"
                value={verification.country || 'Canada'}
                disabled={disabled}
                onChange={(e) => setField('country', e.target.value)}
                placeholder={t('profile.addressCountry')}
                className="w-full px-3 py-2 border rounded text-sm"
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
